import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import TrialLockBanner from "../components/TrialLockBanner";
import { useTrialStatus } from "../hooks/useTrialStatus";
import { useAuth } from "../context/AuthContext";
import { API } from "../constants";

const PLATFORM_ICONS = {
  extension: { label: "Chrome Extension", color: "#4285F4" },
  electron: { label: "Desktop App", color: "#A3E635" },
  mobile: { label: "Mobile App", color: "#F59E0B" },
  web: { label: "Web", color: "#8B5CF6" },
};

const SOURCE_LABELS = {
  tab: "Tab Capture",
  url: "Website",
  video: "Video",
  screen: "Screen",
};

export default function PipDashboardPage() {
  const { user, token } = useAuth();
  const { trial } = useTrialStatus();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pip/sessions`, { headers });
      setSessions(res.data.items || []);
    } catch {
      /* silent - user may not be logged in */
    }
  }, [token]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadSessions, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadSessions]);

  const closeSession = async (id) => {
    try {
      await axios.delete(`${API}/pip/sessions/${id}`, { headers });
      toast.success("PiP session closed");
      loadSessions();
    } catch {
      toast.error("Failed to close session");
    }
  };

  const closeAllSessions = async () => {
    try {
      const res = await axios.post(`${API}/pip/sessions/close-all`, {}, { headers });
      toast.success(`Closed ${res.data.closed} sessions`);
      loadSessions();
    } catch {
      toast.error("Failed to close all");
    }
  };

  const toggleMute = async (id, currentMuted) => {
    try {
      await axios.put(`${API}/pip/sessions/${id}`, { audio_muted: !currentMuted }, { headers });
      loadSessions();
    } catch {
      toast.error("Failed to toggle audio");
    }
  };

  const muteAll = async () => {
    try {
      const anyUnmuted = sessions.some((s) => !s.audio_muted);
      if (anyUnmuted) {
        await axios.post(`${API}/pip/sessions/mute-all`, {}, { headers });
        toast.success("All sessions muted");
      } else {
        await axios.post(`${API}/pip/sessions/unmute-all`, {}, { headers });
        toast.success("All sessions unmuted");
      }
      loadSessions();
    } catch {
      toast.error("Failed to mute all");
    }
  };

  const activeSessions = sessions.filter((s) => s.status === "active");
  const platforms = [...new Set(activeSessions.map((s) => s.platform))];

  // Group by platform
  const grouped = {};
  activeSessions.forEach((s) => {
    if (!grouped[s.platform]) grouped[s.platform] = [];
    grouped[s.platform].push(s);
  });

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <TrialLockBanner trial={trial} message="Your existing PiP sessions stay visible. Creating new sessions or scheduling requires lifetime access." />
          <div className="flex items-center justify-between mb-8" data-testid="pip-dashboard-header">
            <div>
              <h1 className="font-heading text-4xl font-bold text-white" data-testid="pip-dashboard-title">
                PiP Dashboard
              </h1>
              <p className="text-[#E8E4DC] mt-1">
                Manage your active Picture-in-Picture sessions across all devices
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-[#E8E4DC] cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="accent-[#A3E635]"
                />
                Live
              </label>
              <button onClick={loadSessions} className="btn-secondary text-sm" data-testid="pip-refresh-btn">
                Refresh
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="pip-stats">
            <div className="card-glass p-4 text-center">
              <p className="text-3xl font-bold text-[#A3E635]">{activeSessions.length}</p>
              <p className="text-[#E8E4DC] text-sm mt-1">Active Sessions</p>
            </div>
            <div className="card-glass p-4 text-center">
              <p className="text-3xl font-bold text-white">{platforms.length}</p>
              <p className="text-[#E8E4DC] text-sm mt-1">Active Platforms</p>
            </div>
            <div className="card-glass p-4 text-center">
              <p className="text-3xl font-bold text-white">
                {activeSessions.filter((s) => !s.audio_muted).length}
              </p>
              <p className="text-[#E8E4DC] text-sm mt-1">Audio Active</p>
            </div>
            <div className="card-glass p-4 text-center">
              <p className="text-3xl font-bold text-white">
                {activeSessions.filter((s) => s.audio_muted).length}
              </p>
              <p className="text-[#E8E4DC] text-sm mt-1">Muted</p>
            </div>
          </div>

          {/* Global Controls */}
          {activeSessions.length > 0 && (
            <div className="flex gap-3 mb-6" data-testid="pip-global-controls">
              <button
                onClick={muteAll}
                className="btn-secondary text-sm flex items-center gap-2"
                data-testid="pip-mute-all-btn"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  {sessions.some((s) => !s.audio_muted) ? (
                    <>
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </>
                  ) : (
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  )}
                </svg>
                {sessions.some((s) => !s.audio_muted) ? "Mute All" : "Unmute All"}
              </button>
              <button
                onClick={closeAllSessions}
                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                data-testid="pip-close-all-btn"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
                Close All
              </button>
            </div>
          )}

          {/* Sessions by Platform */}
          {activeSessions.length === 0 ? (
            <div className="card-glass p-12 text-center" data-testid="pip-empty">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#E8E4DC]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                </svg>
              </div>
              <h3 className="text-white text-lg font-semibold mb-2">No Active PiP Sessions</h3>
              <p className="text-[#E8E4DC] text-sm max-w-md mx-auto">
                Open a PiP window from the Chrome Extension, Desktop App, or Mobile App to see it here.
                Sessions appear automatically and update in real-time.
              </p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="pip-sessions-list">
              {Object.entries(grouped).map(([platform, platSessions]) => {
                const info = PLATFORM_ICONS[platform] || { label: platform, color: "#888" };
                return (
                  <div key={platform} data-testid={`pip-platform-${platform}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: info.color }}
                      />
                      <h3 className="text-white font-semibold text-sm">{info.label}</h3>
                      <span className="text-[#E8E4DC] text-xs">({platSessions.length})</span>
                    </div>
                    <div className="grid gap-3">
                      {platSessions.map((s) => (
                        <div
                          key={s.id}
                          className="card-glass p-4 flex items-center gap-4 hover:border-[#A3E635]/20 transition-colors"
                          data-testid={`pip-session-${s.id}`}
                        >
                          {/* Source type badge */}
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${info.color}15` }}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke={info.color}
                              strokeWidth="1.5"
                            >
                              {s.source_type === "tab" ? (
                                <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              ) : s.source_type === "url" ? (
                                <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                              ) : s.source_type === "video" ? (
                                <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                              ) : (
                                <rect x="2" y="3" width="20" height="14" rx="2" />
                              )}
                            </svg>
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium truncate">
                                {s.title || s.url || "PiP Session"}
                              </p>
                              <span
                                className="text-xs px-2 py-0.5 rounded-full border"
                                style={{
                                  color: info.color,
                                  borderColor: `${info.color}30`,
                                  backgroundColor: `${info.color}10`,
                                }}
                              >
                                {SOURCE_LABELS[s.source_type] || s.source_type}
                              </span>
                            </div>
                            <div className="flex gap-4 mt-1">
                              {s.url && (
                                <span className="text-[#E8E4DC] text-xs truncate max-w-[200px]">
                                  {s.url}
                                </span>
                              )}
                              <span className="text-[#E8E4DC] text-xs">
                                {new Date(s.created_at).toLocaleTimeString()}
                              </span>
                            </div>
                          </div>

                          {/* Audio + Close Controls */}
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => toggleMute(s.id, s.audio_muted)}
                              className={`p-2 rounded-lg transition-colors ${
                                s.audio_muted
                                  ? "text-red-400 bg-red-500/10 hover:bg-red-500/20"
                                  : "text-[#E8E4DC] hover:text-[#A3E635] hover:bg-[#A3E635]/10"
                              }`}
                              title={s.audio_muted ? "Unmute" : "Mute"}
                              data-testid={`pip-toggle-audio-${s.id}`}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                {s.audio_muted ? (
                                  <>
                                    <line x1="23" y1="9" x2="17" y2="15" />
                                    <line x1="17" y1="9" x2="23" y2="15" />
                                  </>
                                ) : (
                                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                )}
                              </svg>
                            </button>
                            <button
                              onClick={() => closeSession(s.id)}
                              className="p-2 rounded-lg text-[#E8E4DC] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Close"
                              data-testid={`pip-close-session-${s.id}`}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
